// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Building2, 
  CreditCard, 
  Wallet,
  Archive,
  Eye,
  Briefcase
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useAccountsStore } from '@/store/accountsStore'
import { useTransactionStore } from '@/store/transactionsStore'
import { Account } from '@/types/base'
import EditAccount from './EditAccount'
import { toast } from "sonner";
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useSettingsStore } from '@/store/settingsStore'
import { formatCurrency } from '@/utils/formatters'

const accountIcons = {
  bank: Building2,
  credit: CreditCard,
  cash: Wallet,
  checking: Building2,
  savings: Building2,
  investment: Briefcase,
  loan: Building2,
  other: MoreHorizontal
} as const

type AccountType = keyof typeof accountIcons;

interface AccountCardProps {
  account: Account
}

export default function AccountCard({ account }: AccountCardProps) {
  const { t } = useTranslation()
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const { deleteAccount, updateAccount, refreshAccountBalance } = useAccountsStore()
  const { settings } = useSettingsStore();
  
  // Use server-calculated balance from account_balances_view - NO client-side calculations
  const displayBalance = account.balance || 0;
  
  // Ensure account has a currency code
  const currencyCode = account.currency || 'USD';

  // Format amount with user preferences
  const formatAmount = (amount: number, currencyCode: string) => {
    // Use the user's preferred number format from settings
    const numberFormat = settings?.number_format || '#,##0.00';
    
    return formatCurrency(amount, currencyCode, {
      showSymbol: true,
      showCode: false,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      numberFormat: numberFormat
    });
  };

  // Server-Side Balance Strategy:
  // - Account balance comes directly from account_balances_view.current_balance
  // - NO client-side calculations or fallbacks to transaction store
  // - Ensures 100% consistency with database and mobile platform

  // Map 'bank' type to 'checking' for icon display
  let displayType = account.type as AccountType;
  if (displayType === 'bank') {
    displayType = 'checking';
  }

  const Icon = accountIcons[displayType] || accountIcons.other

  const handleDelete = async () => {
    try {
      await deleteAccount(account.id)
      toast.success("Account deleted successfully");
    } catch (error) {
      toast.error("Failed to delete account");
    }
  }

  const handleArchiveToggle = async () => {
    try {
      await updateAccount(account.id, {
        isArchived: !account.isArchived
      })
      toast.success("Account archived successfully");
      setShowArchiveDialog(false)
    } catch (error) {
      toast.error("Failed to archive account");
    }
  }

  return (
    <>
      <Card className={cn(
        "group relative overflow-hidden bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark hover:border-secondary/50 dark:hover:border-primary-dark/50 transition-all duration-200 backdrop-blur-xl backdrop-saturate-150 shadow-sm rounded-xl",
        account.isArchived && "opacity-70"
      )}>
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 dark:from-primary-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        
        <div className="p-5 relative">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 dark:bg-primary-dark/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-secondary dark:text-primary-dark" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-text-primary dark:text-text-dark-primary">
                    {account.name}
                  </h3>
                  {account.isDefault && (
                    <Badge variant="outline" className="bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark border-secondary/30 dark:border-primary-dark/30 text-xs px-1.5 py-0 h-5">
                      {t('accounts.default')}
                    </Badge>
                  )}
                  {account.isArchived && (
                    <Badge variant="outline" className="bg-border/50 dark:bg-border-dark/50 text-text-secondary dark:text-text-dark-secondary border-border/30 dark:border-border-dark/30 text-xs px-1.5 py-0 h-5">
                      {t('accounts.archived')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {t(`accounts.types.${displayType}`)}
                </p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm h-8 w-8"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                <DropdownMenuItem onClick={() => setShowEditDialog(true)} className="text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10">
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('accounts.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowArchiveDialog(true)} className="text-text-secondary dark:text-text-dark-secondary hover:bg-border/10 dark:hover:bg-border-dark/10">
                  {account.isArchived ? (
                    <>
                      <Eye className="mr-2 h-4 w-4" />
                      {t('common.unarchive')}
                    </>
                  ) : (
                    <>
                      <Archive className="mr-2 h-4 w-4" />
                      {t('common.archive')}
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDelete} className="text-error hover:bg-error/10">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('accounts.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                {t('accounts.balance')}
              </p>
              <h4 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                {formatAmount(displayBalance, currencyCode)}
              </h4>
            </div>
          </div>
        </div>

        <EditAccount
          account={account}
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
        />
      </Card>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {account.isArchived ? t('accounts.unarchive.dialogTitle') : t('accounts.archive.dialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {account.isArchived 
                ? t('accounts.unarchive.dialogDescription', { name: account.name }) 
                : t('accounts.archive.dialogDescription', { name: account.name })
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark text-text-secondary dark:text-text-dark-secondary">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleArchiveToggle}
              className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white"
            >
              {account.isArchived ? t('common.unarchive') : t('common.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
